import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  BackgroundContext,
  backgroundUrl,
  isJpegFile,
  BACKGROUND_FILE_ERROR,
  BACKGROUND_NAME_MAX_LENGTH,
} from '../DataContexts/BackgroundContext';
import './BackgroundEditor.css';

/** Default display name of an image: its file name without the extension (mirrors the server fallback). */
export const defaultDisplayName = (fileName) => {
  const base = String(fileName ?? '').trim();
  const dot = base.lastIndexOf('.');
  const stem = dot > 0 ? base.slice(0, dot).trim() : base;
  return stem || base;
};

export const formatUploadedAt = (iso) => (iso ? new Date(iso).toLocaleString('nl-BE') : '—');

const machinesLabel = (n) => `${n} machine${n === 1 ? '' : 's'}`;
const NAME_REQUIRED = 'A background needs a name.';

// Media Studio > Background Editor (DJB-227, r2 in DJB-232/DJB-236: managed
// backgrounds only, no image pool). Left: every managed background with its
// name, upload date and how many machines show it. Right: the create form
// (name + JPG file) or the selected background's details with Rename and
// Delete. The image URL is implicit (backgrounds/<id>.jpg) — nothing is
// picked from a pool, the file is posted straight to POST /backgrounds.
const BackgroundEditor = () => {
  const { backgrounds, loading, error, fetchBackgrounds, createBackground, renameBackground, deleteBackground } =
    useContext(BackgroundContext);

  const [selectedId, setSelectedId] = useState(null);

  // Create form
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const previewRef = useRef(null);

  // Rename
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // The list is not loaded on app start; fetch it when the editor mounts.
  useEffect(() => {
    fetchBackgrounds();
  }, [fetchBackgrounds]);

  const revokePreview = () => {
    if (previewRef.current && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(previewRef.current);
    previewRef.current = null;
  };
  useEffect(() => () => revokePreview(), []);

  const selected = useMemo(() => backgrounds.find((bg) => bg.id === selectedId) ?? null, [backgrounds, selectedId]);

  const resetCreate = () => {
    revokePreview();
    setIsCreating(false);
    setName('');
    setFile(null);
    setPreviewUrl(null);
    setFormError(null);
  };

  const select = (id) => {
    resetCreate();
    setIsEditing(false);
    setEditError(null);
    setConfirmDelete(false);
    setSelectedId(id);
  };

  const startCreate = () => {
    setSelectedId(null);
    setIsEditing(false);
    setConfirmDelete(false);
    setIsCreating(true);
    setFormError(null);
  };

  const handleFileChange = (event) => {
    const chosen = event.target.files?.[0] ?? null;
    revokePreview();
    setPreviewUrl(null);
    if (!chosen) {
      setFile(null);
      return;
    }
    if (!isJpegFile(chosen)) {
      setFile(null);
      setFormError(BACKGROUND_FILE_ERROR);
      return;
    }
    setFormError(null);
    setFile(chosen);
    // Suggest the file name (without extension) while the admin has not typed a name
    setName((current) => (current.trim() ? current : defaultDisplayName(chosen.name)));
    if (typeof URL.createObjectURL === 'function') {
      previewRef.current = URL.createObjectURL(chosen);
      setPreviewUrl(previewRef.current);
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!file) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError(NAME_REQUIRED);
      return;
    }
    setSubmitting(true);
    try {
      const created = await createBackground(file, trimmed);
      if (created) {
        resetCreate();
        setSelectedId(created.id);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const startRename = () => {
    setEditName(selected?.name ?? '');
    setEditError(null);
    setIsEditing(true);
  };

  const handleRename = async (event) => {
    event.preventDefault();
    const trimmed = editName.trim();
    if (!trimmed) {
      setEditError(NAME_REQUIRED);
      return;
    }
    setSaving(true);
    try {
      const updated = await renameBackground(selected.id, trimmed);
      if (updated) {
        setIsEditing(false);
        setEditError(null);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const ok = await deleteBackground(selected.id);
      if (ok) {
        setSelectedId(null);
        setConfirmDelete(false);
      }
    } finally {
      setDeleting(false);
    }
  };

  const renderList = () => {
    if (loading) return <p className="ps-empty">Loading backgrounds…</p>;
    if (error) return <p className="ps-status ps-status--error" role="alert">{error}</p>;
    if (backgrounds.length === 0) return <p className="ps-empty">No backgrounds yet.</p>;
    return (
      <ul className="bg-editor-list">
        {backgrounds.map((bg) => (
          <li key={bg.id}>
            <button
              type="button"
              className={`bg-editor-item${bg.id === selectedId ? ' selected' : ''}`}
              aria-pressed={bg.id === selectedId}
              onClick={() => select(bg.id)}
            >
              <img className="bg-editor-thumb" src={backgroundUrl(bg.id)} alt="" data-testid="bg-thumb" />
              <span className="bg-editor-item-text">
                <span className="bg-editor-item-name" title={bg.name}>{bg.name}</span>
                <span className="bg-editor-item-meta">{formatUploadedAt(bg.uploadedAt)}</span>
                <span className="bg-editor-item-meta">{machinesLabel(bg.subscribedMachineIds.length)}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    );
  };

  const renderCreate = () => (
    <form className="bg-editor-form" onSubmit={handleCreate}>
      <div className="ps-selected">
        <div className="ps-selected-name">New background</div>
      </div>

      <div className="bg-editor-field">
        <label className="ps-form-label" htmlFor="bg-create-name">Name</label>
        <input
          id="bg-create-name"
          type="text"
          className="ps-input"
          value={name}
          maxLength={BACKGROUND_NAME_MAX_LENGTH}
          autoComplete="off"
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="bg-editor-field">
        <label className="ps-form-label" htmlFor="bg-create-file">JPG image</label>
        <input
          id="bg-create-file"
          type="file"
          accept="image/jpeg,.jpg,.jpeg"
          onChange={handleFileChange}
          disabled={submitting}
        />
      </div>

      {previewUrl && (
        <div className="bg-editor-image-wrap">
          <img className="bg-editor-image" src={previewUrl} alt="Preview" />
        </div>
      )}

      {formError && (
        <p className="ps-status ps-status--error" role="alert">{formError}</p>
      )}

      <div className="ps-form-actions">
        <button type="submit" className="ps-btn ps-btn--success" disabled={!file || submitting}>
          {submitting ? 'Uploading…' : 'Upload'}
        </button>
        <button type="button" className="ps-btn ps-btn--ghost" onClick={resetCreate} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );

  const renderDetails = (bg) => (
    <div className="bg-editor-details">
      {isEditing ? (
        <form className="bg-editor-form" onSubmit={handleRename}>
          <div className="bg-editor-field">
            <label className="ps-form-label" htmlFor="bg-edit-name">Name</label>
            <input
              id="bg-edit-name"
              type="text"
              className="ps-input"
              value={editName}
              maxLength={BACKGROUND_NAME_MAX_LENGTH}
              autoComplete="off"
              autoFocus
              onChange={(e) => setEditName(e.target.value)}
              disabled={saving}
            />
          </div>
          {editError && <p className="ps-status ps-status--error" role="alert">{editError}</p>}
          <div className="ps-form-actions">
            <button type="submit" className="ps-btn ps-btn--success" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="ps-btn ps-btn--ghost" onClick={() => setIsEditing(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <h3>{bg.name}</h3>
      )}

      <div className="bg-editor-image-wrap">
        <img className="bg-editor-image" src={backgroundUrl(bg.id)} alt={bg.name} />
      </div>

      <dl className="bg-editor-meta">
        <dt>Uploaded</dt>
        <dd>{formatUploadedAt(bg.uploadedAt)}</dd>
        <dt>Shown on</dt>
        <dd>{machinesLabel(bg.subscribedMachineIds.length)}</dd>
      </dl>

      {confirmDelete ? (
        <div className="bg-editor-confirm">
          <p>
            Delete “{bg.name}”? {machinesLabel(bg.subscribedMachineIds.length)} will fall back to the default background.
          </p>
          <div className="ps-form-actions">
            <button type="button" className="ps-btn ps-btn--danger-solid" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Yes, delete'}
            </button>
            <button type="button" className="ps-btn ps-btn--ghost" onClick={() => setConfirmDelete(false)} disabled={deleting}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        !isEditing && (
          <div className="bg-editor-details-actions">
            <button type="button" className="ps-btn ps-btn--ghost" onClick={startRename}>Rename</button>
            <button type="button" className="ps-btn ps-btn--danger" onClick={() => setConfirmDelete(true)}>Delete</button>
          </div>
        )
      )}
    </div>
  );

  return (
    <div className="bg-editor">
      <div className="ps-columns bg-editor-columns">
        <div className="ps-column ps-column--playlists bg-editor-list-column">
          <div className="ps-column-head">
            <span className="ps-column-label">Backgrounds</span>
            <span className="ps-column-count">{backgrounds.length}</span>
          </div>
          <div className="ps-toolbar">
            <button type="button" className="ps-btn ps-btn--primary" onClick={startCreate}>Create</button>
          </div>
          <div className="ps-list-scroll">{renderList()}</div>
        </div>

        <div className="ps-column bg-editor-detail-column">
          {isCreating ? renderCreate() : selected ? renderDetails(selected) : (
            <div className="ps-placeholder">Select a background or create a new one.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackgroundEditor;
